import React, { useEffect, useMemo, useState } from 'react';
import { IconSearch } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import OperatorActionCard from '@/components/haccp/OperatorActionCard';
import ProductThumbnail from '@/components/haccp/ProductThumbnail';
import StatusBadge from '@/components/haccp/StatusBadge';
import { Input } from '@/components/ui/input';
import { LoadingState } from '@/components/haccp/AsyncState';
import { DEFAULT_DASHBOARD_CARDS, getVisibleDashboardCards } from '@/config/dashboardCards';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user, hasFeature } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [expiringLots, setExpiringLots] = useState([]);
  const [activities, setActivities] = useState([]);
  const [dashboardCards, setDashboardCards] = useState(DEFAULT_DASHBOARD_CARDS);
  const [traceQuery, setTraceQuery] = useState('');
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState('');

  const canTrace = hasFeature('traceability.advanced');
  const canProduce = hasFeature('production.manage');
  const canRegister = hasFeature('haccp.register');

  const load = async () => {
    setDataLoading(true);
    setDataError('');
    const requests = [
      fetch(`${BACKEND_URL}/api/admin/dashboard-stats`, { credentials: 'include' }),
      fetch(`${BACKEND_URL}/api/lots/expiring/soon?days=7`, { credentials: 'include' }),
      fetch(`${BACKEND_URL}/api/companies/me`, { credentials: 'include' }),
      canRegister ? fetch(`${BACKEND_URL}/api/movements/audit-log`, { credentials: 'include' }) : Promise.resolve(null),
    ];

    const [statsResult, expiringResult, companyResult, activityResult] = await Promise.allSettled(requests);

    try {
      if (companyResult.status === 'fulfilled' && companyResult.value?.ok) {
        const company = await companyResult.value.json();
        setDashboardCards(company?.dashboard_cards || DEFAULT_DASHBOARD_CARDS);
      }
      if (expiringResult.status === 'fulfilled' && expiringResult.value?.ok) setExpiringLots(await expiringResult.value.json());
      if (activityResult.status === 'fulfilled' && activityResult.value?.ok) setActivities((await activityResult.value.json()).slice(0, 4));

      if (statsResult.status !== 'fulfilled' || !statsResult.value?.ok) throw new Error('Impossibile caricare i dati riepilogativi');
      setStats(await statsResult.value.json());
    } catch (error) {
      setDataError(error.message || 'Impossibile caricare i dati riepilogativi');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => { load(); }, [canRegister]); // eslint-disable-line react-hooks/exhaustive-deps

  const priorities = useMemo(() => buildPriorities(expiringLots), [expiringLots]);
  const visibleCards = useMemo(() => getVisibleDashboardCards(dashboardCards, hasFeature), [dashboardCards, hasFeature]);

  const searchTraceability = (event) => {
    event?.preventDefault();
    const query = traceQuery.trim();
    navigate(`/registro?tab=traceability${query ? `&q=${encodeURIComponent(query)}` : ''}`);
  };

  return (
    <div data-testid="dashboard" className="app-page klaro-dashboard-page">
      <div className="app-page-inner">
        <section className="klaro-dashboard-heading">
          <div className="min-w-0">
            <p className="hidden font-heading text-[32px] font-semibold leading-tight tracking-tight text-[#0A1737] sm:block lg:text-[36px]">Dashboard</p>
            <p className="font-heading text-[30px] font-semibold leading-tight tracking-tight text-[#0A1737] sm:hidden">Ciao, {user?.name?.split(' ')[0] || 'Operatore'}</p>
            <p className="mt-1 text-[15px] text-[#7B8AA7] sm:text-base">Gestisci con semplicità la sicurezza alimentare.</p>
          </div>

          {canTrace && (
            <form onSubmit={searchTraceability} className="klaro-dashboard-search">
              <IconSearch className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#0A1737]" stroke={1.9} />
              <Input
                value={traceQuery}
                onChange={(event) => setTraceQuery(event.target.value)}
                className="h-12 rounded-xl border-[#DEE4ED] bg-white pl-11 pr-4 text-sm shadow-[0_1px_3px_rgba(16,24,40,.04)]"
                placeholder="Cerca lotto, prodotto o ingrediente..."
                aria-label="Cerca nella tracciabilità"
              />
            </form>
          )}
        </section>

        <section aria-label="Funzioni principali" className="klaro-dashboard-grid">
          {visibleCards.map((card) => (
            <OperatorActionCard
              key={card.id}
              title={card.title}
              description={card.description}
              icon={card.icon}
              mobileWide={card.mobileWide}
              onClick={() => navigate(card.route)}
            />
          ))}
        </section>

        <section className={`klaro-dashboard-lower ${canRegister ? '' : 'klaro-dashboard-lower--two'}`}>
          <div className="surface klaro-overview-card">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div><h2 className="section-title">Panoramica rapida</h2><p className="mt-1 text-xs text-[#7B8AA7]">Dati correnti dell’azienda.</p></div>
            </div>
            {dataLoading && !stats ? <LoadingState compact /> : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
                <Metric label="Lotti attivi" value={stats?.active_lots ?? '—'} />
                <Metric label="Scadenze 7 gg" value={stats?.expiring_lots ?? expiringLots.length ?? '—'} />
                {canProduce && <Metric label="Produzioni oggi" value={stats?.batches_today ?? '—'} />}
                <Metric label="Carichi oggi" value={stats?.lots_loaded_today ?? '—'} />
              </div>
            )}
            {dataError && <p className="mt-3 text-xs text-red-600">{dataError}</p>}
          </div>

          <div className="surface overflow-hidden">
            <div className="flex items-center justify-between gap-3 border-b border-[#EEF1F5] px-4 py-3.5">
              <div><h2 className="section-title">Da controllare</h2><p className="mt-1 text-xs text-[#7B8AA7]">Scadenze più vicine.</p></div>
              <button type="button" onClick={() => navigate('/scadenzario')} className="text-sm font-semibold text-[#119B49] hover:underline">Vedi tutte</button>
            </div>
            <div className="divide-y divide-[#EEF1F5]">
              {priorities.length ? priorities.slice(0, 4).map((lot) => (
                <button key={lot.lot_id || lot.lot_number} type="button" onClick={() => navigate('/scadenzario')} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50">
                  <ProductThumbnail item={lot} size="table" className="h-10 w-10" />
                  <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-[#0A1737]">{lot.ingredient_name || lot.commercial_name || lot.output_product_name || 'Prodotto'}</span><span className="mt-0.5 block truncate text-xs text-[#7B8AA7]">{lot.lot_number || 'Lotto senza codice'} · {lot.expiryLabel}</span></span>
                  <StatusBadge status={lot.status} label={lot.statusLabel} className="shrink-0" />
                </button>
              )) : (
                <div className="flex min-h-28 items-center gap-3 px-4 py-5"><StatusBadge status="compliant" label="Nessuna criticità" /><p className="text-sm text-[#7B8AA7]">Nessun lotto scaduto o in scadenza nei prossimi 7 giorni.</p></div>
              )}
            </div>
          </div>

          {canRegister && (
            <div className="surface overflow-hidden">
              <div className="flex items-center justify-between gap-3 border-b border-[#EEF1F5] px-4 py-3.5">
                <div><h2 className="section-title">Ultime attività</h2><p className="mt-1 text-xs text-[#7B8AA7]">Dal Registro HACCP.</p></div>
                <button type="button" onClick={() => navigate('/registro')} className="text-sm font-semibold text-[#119B49] hover:underline">Vedi tutte</button>
              </div>
              <div className="divide-y divide-[#EEF1F5]">
                {activities.length ? activities.map((entry) => (
                  <button key={entry.audit_id || `${entry.timestamp}-${entry.entity_id}`} type="button" onClick={() => navigate('/registro')} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#27C25B]" />
                    <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-[#34425C]">{activityLabel(entry)}</span><span className="mt-0.5 block truncate text-xs text-[#7B8AA7]">{entry.entity_id || entry.entity || 'Registro HACCP'}</span></span>
                    <time className="shrink-0 text-[11px] text-[#98A2B3]">{entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : ''}</time>
                  </button>
                )) : <div className="flex min-h-28 items-center px-4 py-5 text-sm text-[#7B8AA7]">Nessuna attività recente.</div>}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return <div className="rounded-xl border border-[#EEF1F5] bg-[#FBFCFE] p-3 text-center"><p className="font-heading text-2xl font-semibold text-[#0A1737]">{value}</p><p className="mt-1 text-xs text-[#7B8AA7]">{label}</p></div>;
}

function activityLabel(entry) {
  const labels = {
    lot_created: 'Lotto creato',
    lot_use: 'Utilizzo lotto',
    lot_load: 'Lotto caricato',
    lot_adjustment: 'Rettifica lotto',
    lot_created_from_ocr: 'Lotto creato da OCR',
    batch_created: 'Produzione registrata',
    ocr_upload: 'Documento OCR acquisito',
  };
  return labels[entry.action] || entry.action || 'Attività registrata';
}

function buildPriorities(lots) {
  if (!Array.isArray(lots)) return [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return lots.map((lot) => {
    const expiry = new Date(lot.expiry_date);
    expiry.setHours(0, 0, 0, 0);
    const diff = Math.round((expiry - today) / 86400000);
    if (diff < 0) return { ...lot, sort: diff, status: 'expired', statusLabel: 'Scaduto', expiryLabel: `scaduto da ${Math.abs(diff)} gg` };
    if (diff === 0) return { ...lot, sort: 0, status: 'expiring', statusLabel: 'Scade oggi', expiryLabel: 'scade oggi' };
    if (diff === 1) return { ...lot, sort: 1, status: 'expiring', statusLabel: 'Scade domani', expiryLabel: 'scade domani' };
    return { ...lot, sort: diff, status: 'expiring', statusLabel: `Tra ${diff} gg`, expiryLabel: `scade tra ${diff} giorni` };
  }).sort((a, b) => a.sort - b.sort);
}
